import type { IAgentToolContext, IAgentTool } from '../../../../core/ai/agent/IAgentTool';
import { useDiagramStore } from '../../../../stores/diagramStore';
import { PROCESS_STEP_TYPES, getProcessStepDuration } from '../../utils/processDuration';

export const criticalPathTool: IAgentTool = {
  name: 'process_critical_path',
  description: 'Find the critical path (longest duration path) through the process. Shows total time and bottleneck step. Use when the user asks about critical path, total duration, bottleneck, or time analysis.',
  parameters: {
    type: 'object',
    properties: {},
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'safe',
  category: 'query',

  async execute(_params: Record<string, unknown>, context: IAgentToolContext) {
    const nodes = context.diagramData.nodes;
    const edges = context.diagramData.edges;

    const steps = nodes.filter((n) => PROCESS_STEP_TYPES.has(n.type));
    if (steps.length === 0) {
      return { success: false, summary: 'Add connected process steps before asking for a critical path.' };
    }
    const getAttr = (n: typeof nodes[0], key: string): unknown =>
      n.attrs?.[key] ?? n.props?.[key];

    const durationMap = new Map<string, number>();
    const missingStepIds: string[] = [];
    for (const step of steps) {
      const dur = getProcessStepDuration(step);
      if (dur !== null) {
        durationMap.set(step.id, dur);
      } else {
        missingStepIds.push(step.id);
      }
    }

    if (missingStepIds.length > 0) {
      return {
        success: false,
        summary: 'Duration estimates are needed before I can find the critical path. Select a process step, open its Properties panel, and enter Duration (for example, 15 min or 2 hr). Enter an estimate for every step (use 0 for steps with no elapsed time), then ask me to find bottlenecks again.',
        data: { needsInput: true, missingField: 'duration', stepIds: missingStepIds },
      };
    }

    // Build adjacency
    const outgoing = new Map<string, string[]>();
    const stepIds = new Set(steps.map((n) => n.id));
    for (const edge of edges) {
      if (!stepIds.has(edge.from) || !stepIds.has(edge.to)) continue;
      const list = outgoing.get(edge.from) ?? [];
      list.push(edge.to);
      outgoing.set(edge.from, list);
    }

    // Topological sort + longest path
    const inDegree = new Map<string, number>();
    for (const id of stepIds) inDegree.set(id, 0);
    for (const [, tos] of outgoing) {
      for (const to of tos) {
        if (stepIds.has(to)) inDegree.set(to, (inDegree.get(to) ?? 0) + 1);
      }
    }

    const longestTo = new Map<string, { duration: number; path: string[] }>();
    const queue: string[] = [];
    for (const [id, deg] of inDegree) {
      if (deg === 0) {
        queue.push(id);
        longestTo.set(id, { duration: durationMap.get(id) ?? 0, path: [id] });
      }
    }

    let visitedCount = 0;
    while (queue.length > 0) {
      const current = queue.shift()!;
      visitedCount += 1;
      const currentInfo = longestTo.get(current) ?? { duration: 0, path: [current] };

      for (const next of outgoing.get(current) ?? []) {
        if (!stepIds.has(next)) continue;
        const nextDur = currentInfo.duration + (durationMap.get(next) ?? 0);
        const existing = longestTo.get(next);
        if (!existing || nextDur > existing.duration) {
          longestTo.set(next, { duration: nextDur, path: [...currentInfo.path, next] });
        }
        const newDeg = (inDegree.get(next) ?? 1) - 1;
        inDegree.set(next, newDeg);
        if (newDeg === 0) queue.push(next);
      }
    }

    if (visitedCount !== steps.length) {
      return {
        success: false,
        summary: 'This process contains a loop. A finite critical path cannot be calculated without a bounded loop model. Review the loop before rerunning the analysis.',
        data: { needsInput: true, reason: 'process_cycle' },
      };
    }

    // Find max
    let maxDuration = 0;
    let maxPath: string[] = [];
    for (const [, info] of longestTo) {
      if (info.duration > maxDuration) {
        maxDuration = info.duration;
        maxPath = info.path;
      }
    }

    if (maxDuration === 0) {
      return { success: true, summary: 'All steps have 0 duration.' };
    }

    const hours = Math.floor(maxDuration / 60);
    const mins = Math.round(maxDuration % 60);
    const formatted = hours > 0 ? `${hours}h ${mins}m` : `${mins} minutes`;

    // Find bottleneck
    let bottleneckId = maxPath[0];
    let bottleneckDur = 0;
    for (const id of maxPath) {
      const dur = durationMap.get(id) ?? 0;
      if (dur > bottleneckDur) {
        bottleneckDur = dur;
        bottleneckId = id;
      }
    }

    const bottleneckNode = steps.find((n) => n.id === bottleneckId);
    const bottleneckLabel = bottleneckNode
      ? (getAttr(bottleneckNode, 'label') as string || getAttr(bottleneckNode, 'name') as string || bottleneckNode.name || bottleneckNode.text || bottleneckId)
      : bottleneckId;

    // Build path description
    const pathLabels = maxPath.map((id) => {
      const node = steps.find((n) => n.id === id);
      return node ? (getAttr(node, 'label') as string || getAttr(node, 'name') as string || node.name || node.text || id) : id;
    });

    // Highlight the path if possible
    if (context.stores.highlight?.setHighlightedIds) {
      context.stores.highlight.setHighlightedIds(new Set(maxPath));
    }
    // The highlight store has no consumer on process canvases, so also select
    // the critical-path steps — selection renders for every node type.
    useDiagramStore.getState().setSelected(new Set(maxPath));

    return {
      success: true,
      summary: `Critical path: ${formatted} across ${maxPath.length} steps.\n\nPath: ${pathLabels.join(' → ')}\n\nBottleneck: "${bottleneckLabel}" (${Math.round(bottleneckDur)} min) — reducing this step has the highest impact.`,
      data: {
        totalDuration: maxDuration,
        formatted,
        stepCount: maxPath.length,
        path: maxPath,
        pathLabels,
        bottleneck: { id: bottleneckId, label: bottleneckLabel, duration: bottleneckDur },
      },
    };
  },
};
